import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import DOMPurify from "dompurify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const NIVEIS_OPCOES = [2, 3, 4, 5, 6];

// Sanitização defensiva do SVG antes de injetar via dangerouslySetInnerHTML.
// O SVG vem do endpoint /adesivos/vetorizar (potrace, server-side, mesma
// sessão autenticada que enviou a arte original) e por construção só contém
// <svg>/<path>/<rect> com atributos de geometria/cor — mas não confiamos
// nisso implicitamente: usamos DOMPurify (não regex — uma versão anterior
// baseada em regex removia <script>/on*=/javascript: literais mas tinha
// bypass via SMIL: <animate>/<set> alterando xlink:href dinamicamente para
// javascript:..., achado numa revisão de segurança e reproduzido com
// exploit real via Playwright). USE_PROFILES svg+svgFilters permite as tags
// estruturais do potrace (svg, path, rect, circle, polygon, g) mas bloqueia
// script/animate/set/foreignObject/a e qualquer atributo on*/href com
// javascript:.
export const sanitizarSvg = (raw) => {
  return DOMPurify.sanitize(String(raw), {
    USE_PROFILES: { svg: true, svgFilters: true },
    // "image" também bloqueada: potrace nunca gera essa tag, e sem ela um
    // href/data-URI externo não pode servir como beacon de tracking (achado
    // não-bloqueante da revisão de segurança do fix DOMPurify).
    FORBID_TAGS: ["script", "animate", "animateMotion", "animateTransform", "set", "foreignObject", "a", "image"],
    FORBID_ATTR: ["onload", "onerror", "onclick", "onmouseover"],
  });
};

// Dialog de vetorização assistida (Fase 2 — "Produção sem Corel"): o usuário
// sobe uma logo rasterizada (já enviada como arte, via AdesivoDialog) e aqui
// gera uma versão vetorizada (SVG) via potrace posterizado, compara antes/depois
// e, se aprovar, aceita — o que rasteriza o SVG em alta resolução e substitui
// a arte do formulário (mesma convenção de nome adesivo_arte_*.png).
const VetorizarDialog = ({ open, onClose, arteFileName, onAceitar }) => {
  const [niveis, setNiveis] = useState(3);
  const [vetorizando, setVetorizando] = useState(false);
  const [aceitando, setAceitando] = useState(false);
  const [svg, setSvg] = useState(null);

  useEffect(() => {
    if (!open) {
      setSvg(null);
      setNiveis(3);
    }
  }, [open]);

  const handleVetorizar = async () => {
    if (!arteFileName) return;
    setVetorizando(true);
    try {
      const { data } = await api.post("/adesivos/vetorizar", { arteFileName, niveis });
      setSvg(data.svg);
    } catch (err) { toastError(err); }
    finally { setVetorizando(false); }
  };

  const handleAceitar = async () => {
    if (!svg) return;
    setAceitando(true);
    try {
      const { data } = await api.post("/adesivos/vetorizar/aceitar", { svg });
      onAceitar && onAceitar(data.fileName);
      toast.success("Arte vetorizada aplicada.");
      onClose();
    } catch (err) { toastError(err); }
    finally { setAceitando(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AutoFixHighIcon color="primary" />
          <span>Vetorizar logo (sem Corel)</span>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Converte a arte rasterizada (foto/print) em um vetor por posterização
          (múltiplos níveis de cor) via potrace. Confira o resultado antes de aplicar —
          se não ficar bom, ajuste os níveis de cor e vetorize de novo.
        </Alert>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Níveis de cor</InputLabel>
              <Select
                value={niveis}
                label="Níveis de cor"
                onChange={(e) => setNiveis(e.target.value)}
              >
                {NIVEIS_OPCOES.map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={7}>
            <Button
              variant="outlined"
              fullWidth
              disabled={!arteFileName || vetorizando}
              startIcon={vetorizando ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
              onClick={handleVetorizar}
            >
              Vetorizar
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
              Original
            </Typography>
            <div style={{
              border: "1px solid #ddd", borderRadius: 4, minHeight: 220,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "#fafafa", overflow: "hidden",
            }}>
              {arteFileName ? (
                // eslint-disable-next-line jsx-a11y/alt-text
                <img
                  src={`/adesivos/arte/${arteFileName}`}
                  alt="arte original"
                  style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain" }}
                />
              ) : (
                <Typography variant="caption" color="textSecondary">Sem arte enviada</Typography>
              )}
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
              Vetorizado (SVG)
            </Typography>
            <div style={{
              border: "1px solid #ddd", borderRadius: 4, minHeight: 220,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "#fff", overflow: "hidden", padding: 8, boxSizing: "border-box",
            }}>
              {svg ? (
                <div
                  style={{ width: "100%", height: "100%" }}
                  // O SVG do potrace sempre traz width/height/viewBox explícitos
                  // (px da imagem original), mas queremos que ele preencha o
                  // quadro de preview — width/height 100% no wrapper via CSS
                  // inline no próprio <svg> não é confiável (vem embutido no
                  // markup gerado), então normalizamos com um <div> que força
                  // o <svg> filho a se ajustar.
                  dangerouslySetInnerHTML={{
                    __html: sanitizarSvg(svg).replace("<svg ", '<svg style="width:100%;height:auto;max-height:220px;display:block;margin:auto" '),
                  }}
                />
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {vetorizando ? "Vetorizando..." : "Clique em \"Vetorizar\" para gerar o preview"}
                </Typography>
              )}
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="success"
          disabled={!svg || aceitando}
          startIcon={aceitando ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          onClick={handleAceitar}
        >
          Aceitar e substituir arte
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VetorizarDialog;
